import React, { useState, useEffect } from 'react';
import { Bitcoin, ShoppingCart, CreditCard, Loader } from 'lucide-react';
import { tokenService } from '../../services/tokenService';
import { fetchSubscriptionStatus } from '../../services/userService';

const TokenDisplay = ({ userData, userId }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);

  useEffect(() => {
    if (userId) {
      loadSubscriptionStatus();
    }
  }, [userId]);

  const loadSubscriptionStatus = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const status = await fetchSubscriptionStatus(userId);
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!userId || isPurchasing) return;

    setIsPurchasing(true);
    try {
      const checkoutUrl = await tokenService.purchaseTokens(userId, packageId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert(`Failed to start purchase: ${error.message}`);
    } finally {
      setIsPurchasing(false);
      setShowPackageModal(false);
    }
  };

  const handleSubscribe = async () => {
    if (!userId || isPurchasing) return;

    setIsPurchasing(true);
    try {
      const checkoutUrl = await tokenService.subscribe(userId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        alert('Failed to create subscription. Please try again.');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      alert(`Failed to start subscription: ${error.message}`);
    } finally {
      setIsPurchasing(false);
    }
  };

  const packages = [
    { id: 'small', name: 'Small', tokens: 100, price: '$5' },
    { id: 'medium', name: 'Medium', tokens: 500, price: '$10' },
    { id: 'large', name: 'Large', tokens: 2000, price: '$15' },
  ];

  if (!userId) {
    return (
      <div className="p-4 panel">
        <p className="text-sm text-gray-500 text-center">
          Please log in to view your token balance
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 panel space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
          <Bitcoin className="w-4 h-4 mr-2 text-yellow-500" />
          Web App Tokens
        </h3>
        <span className="text-2xl font-bold text-indigo-600">
          {userData ? userData.tokens.toLocaleString() : '...'}
        </span>
      </div>
      
      <p className="text-xs text-gray-500">
        *Tokens enable premium features in linked web applications.
      </p>

      {/* Subscription Status */}
      {subscriptionStatus?.has_subscription && (
        <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
          <span className="text-green-700 font-medium">✓ Active Subscription</span>
          {subscriptionStatus.tokens_per_month && (
            <span className="text-green-600 ml-2">
              ({subscriptionStatus.tokens_per_month} tokens/month)
            </span>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 pt-2">
        {subscriptionStatus?.has_subscription ? (
          <button
            onClick={() => setShowPackageModal(true)}
            disabled={isPurchasing || isLoading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPurchasing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                <span>Buy Tokens</span>
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleSubscribe}
            disabled={isPurchasing || isLoading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPurchasing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                <span>Subscribe ($4/month)</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Package Selection Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Select Token Package</h3>
            <div className="space-y-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={isPurchasing}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition text-left disabled:opacity-50"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-900">{pkg.name} Package</div>
                      <div className="text-sm text-gray-600">{pkg.tokens.toLocaleString()} tokens</div>
                    </div>
                    <div className="text-lg font-bold text-indigo-600">{pkg.price}</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowPackageModal(false)}
              disabled={isPurchasing}
              className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenDisplay;
